/**
 * GroupCategoryFeedPage (iter83) — single ``ProductCategoryGroup``
 * feed at ``/g/:slug``. Shows products aggregated across every shop
 * whose ProductCategory is mapped to this group, plus a "Shops"
 * tab listing every distinct shop that contributes.
 *
 * Product cards link to ``/product/:pid`` and expose an inline ADD
 * control (iter115) so customers can add items directly from the feed
 * — the cart is still restricted to one shop at a time (backend
 * enforces via a 409 DIFFERENT_RESTAURANT check), so switching shops
 * prompts the customer to replace the cart.
 *
 * Backed by ``GET /api/category-groups/{slug}``.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Package, Store, ChevronRight } from "lucide-react";
import { api, inr } from "../../lib/api";
import RestaurantCard from "../../components/RestaurantCard";
import StockBadge from "../../components/StockBadge";
import InlineCartControl from "../../components/InlineCartControl";
import { useAuth } from "../../contexts/AuthContext";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import BkCatalogView from "./blinkit/BkCatalogView";

// Blinkit-theme tenants open the group inside the category browser; every other theme keeps this page unchanged.
export default function GroupCategoryFeedPage() {
    const { slug } = useParams();
    return useIsBlinkit() ? <BkCatalogView key={slug} groupSlug={slug} /> : <ClassicGroupCategoryFeedPage />;
}

function ClassicGroupCategoryFeedPage() {
    const { slug } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("products");
    const [err, setErr] = useState(null);
    const [cartMap, setCartMap] = useState({});

    useEffect(() => {
        setData(null); setErr(null);
        api.get(`/category-groups/${slug}`)
            .then(({ data: d }) => setData(d))
            .catch((e) => setErr(e?.response?.data?.detail || "Group not found"));
    }, [slug]);

    // Shared cart-qty cache — same pattern as BrowseProductsPage so every
    // inline control on the page reads the correct qty and stays in sync
    // when any of them mutates.
    useEffect(() => {
        let alive = true;
        const load = async () => {
            if (!user) { setCartMap({}); return; }
            try {
                const { data: c } = await api.get("/cart");
                if (!alive) return;
                const m = {};
                for (const it of (c?.items || [])) {
                    m[it.product_id] = (m[it.product_id] || 0) + (it.quantity || 0);
                }
                setCartMap(m);
            } catch {
                if (alive) setCartMap({});
            }
        };
        load();
        window.addEventListener("cart:update", load);
        return () => { alive = false; window.removeEventListener("cart:update", load); };
    }, [user]);

    if (err) {
        return (
            <div className="p-8 text-center" data-testid="group-feed-error">
                <p className="font-semibold">{err}</p>
                <Link to="/" className="inline-block mt-3 text-primary font-bold text-sm">Go home</Link>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="p-8 flex items-center gap-2 text-muted-foreground text-sm">
                <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
        );
    }

    const products = data.products || [];
    const shops = data.shops || [];

    return (
        <div className="pb-32" data-testid="group-feed-page">
            {/* Hero strip */}
            <header className="relative">
                {data.group.image_url ? (
                    <img src={data.group.image_url} alt={data.group.name} className="w-full h-40 md:h-56 object-cover" />
                ) : (
                    <div className="w-full h-32 md:h-44 bg-primary/10" />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-card" />
                <Link to="/" className="absolute top-4 left-4 bg-card/90 backdrop-blur-md rounded-full p-2"><ArrowLeft size={20} /></Link>
                <div className="absolute bottom-3 left-4 right-4">
                    <h1 className="text-lg md:text-2xl font-extrabold text-foreground" data-testid="group-name">{data.group.name}</h1>
                    {data.group.description && (
                        <p className="text-xs md:text-sm text-muted-foreground line-clamp-2">{data.group.description}</p>
                    )}
                </div>
            </header>

            {/* Tabs */}
            <nav className="px-4 mt-3 flex items-center gap-2 border-b border-border" data-testid="group-feed-tabs">
                <button
                    onClick={() => setTab("products")}
                    data-testid="tab-products"
                    className={`px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5 -mb-px border-b-2 ${tab === "products" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}
                >
                    <Package size={14} /> Products ({products.length})
                </button>
                <button
                    onClick={() => setTab("shops")}
                    data-testid="tab-shops"
                    className={`px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5 -mb-px border-b-2 ${tab === "shops" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}
                >
                    <Store size={14} /> Shops ({shops.length})
                </button>
            </nav>

            {tab === "products" ? (
                <section className="px-4 mt-3" data-testid="group-products-list">
                    {products.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center">No products available right now.</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                            {products.map((p) => <ProductRow key={`${p.restaurant_id}-${p.id}`} product={p} cartQty={cartMap[p.id] || 0} />)}
                        </div>
                    )}
                </section>
            ) : (
                <section className="px-4 mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3" data-testid="group-shops-list">
                    {shops.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center col-span-full">No shops mapped to this group yet.</p>
                    ) : shops.map((s) => <RestaurantCard key={s.id} r={s} />)}
                </section>
            )}
        </div>
    );
}

/** Compact product-in-group row that links into the source shop. */
function ProductRow({ product, cartQty = 0 }) {
    const img = product.images?.[0] || product.image_url;
    return (
        <div
            data-testid={`group-product-${product.id}`}
            className="flex gap-3 bg-card border border-border rounded-2xl p-3"
        >
            <Link to={`/product/${product.id}`} className="flex-shrink-0" aria-label={`View ${product.name}`}>
                {img ? (
                    <img src={img} alt={product.name} className="w-20 h-20 object-cover rounded-xl" loading="lazy" />
                ) : (
                    <div className="w-20 h-20 rounded-xl bg-muted inline-flex items-center justify-center text-muted-foreground">
                        <Package size={20} />
                    </div>
                )}
            </Link>
            <div className="flex-1 min-w-0 flex flex-col">
                <Link
                    to={`/product/${product.id}`}
                    data-testid={`group-product-link-${product.id}`}
                    className="min-w-0"
                >
                    <p className="font-bold text-sm leading-tight line-clamp-2">{product.name}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{product.restaurant_name}</p>
                </Link>
                <div className="mt-auto pt-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                        <span className="font-bold text-sm">{inr(product.selling_price)}</span>
                        {product.mrp && product.mrp > product.selling_price && (
                            <span className="text-xs text-muted-foreground line-through">{inr(product.mrp)}</span>
                        )}
                        <StockBadge product={product} />
                    </div>
                    <InlineCartControl product={product} currentQty={cartQty} size="sm" />
                </div>
            </div>
        </div>
    );
}
